/* eslint-disable prettier/prettier */
import { HTTPClient, HTTPMethod, HTTPResponse } from "@/core/common/communication/HTTPClient";
import { UserDS } from "../domain/UserDS";
import UsersRepository from "../domain/UsersRepository";
import {
  LoadUsersResponse,
  UsersOperarionsResponse as UsersOperationsResponse
} from "../domain/UsersOperationsResponse";
import {
  DELETE_USER_ERROR,
  LOADING_USERS_ERROR,
  SAVE_USER_ERROR,
  UPDATE_USER_ERROR
} from "@/core/common/utils/ResourceStrings";

interface APIResponse<T> {
  result: boolean;
  data?: T;
  detail?: string | object;
}

export default class UsersRepositoryImpl extends UsersRepository {
  private httpClient: HTTPClient;
  private allUsers: Map<string, UserDS>;
  private usersLoaded: boolean;
  private activeUsers: Array<string>;

  constructor(httpClient: HTTPClient) {
    super();
    this.httpClient = httpClient;
    this.allUsers = new Map();
    this.activeUsers = new Array<string>();
    this.usersLoaded = false;
  }

  private getDetail(detail: string | object | undefined): string {
    if (!detail) return "";
    return typeof detail === "string" ? detail : JSON.stringify(detail);
  }

  private withDefaultPivotOptions(user: UserDS): UserDS {
    return {
      ...user,
      pivotOptions: user.pivotOptions || ({} as UserDS["pivotOptions"]),
      password: user.password || ""
    };
  }

  private toBackendUserDto(user: UserDS): object {
    return {
      id: user.id,
      name: user.name,
      surname: user.surname,
      type: user.type,
      color: user.color,
      phone: user.phone,
      mail: user.mail,
      deleted: user.deleted,
      password: user.password || undefined
    };
  }

  public getUserById(id: string): UserDS {
    let result = {} as UserDS;

    if (this.allUsers.has(id)) result = this.allUsers.get(id) as UserDS;

    return result;
  }

  private doIndexUsers(users: Array<UserDS>) {
    this.allUsers = new Map();
    this.activeUsers = new Array<string>();
    users.forEach((user) => {
      this.allUsers.set(user.id, user);
      if (!user.deleted) this.activeUsers.push(user.id);
    });
  }

  public async loadUsers(): Promise<LoadUsersResponse> {
    const result = {
      users: new Array<UserDS>(),
      success: true,
      errorMsg: ""
    };

    if (!this.usersLoaded) {
      const httpResponse: HTTPResponse = await this.httpClient.send(HTTPMethod.GET, "/api/user/list", {});
      const apiResponse = httpResponse.data as APIResponse<Array<UserDS>>;

      if (httpResponse.success && apiResponse.result) {
        result.users = (apiResponse.data || new Array<UserDS>()).map((user) => this.withDefaultPivotOptions(user));
        this.doIndexUsers(result.users);
        this.usersLoaded = true;
      } else {
        result.success = false;
        result.errorMsg = httpResponse.success
          ? `${LOADING_USERS_ERROR}${this.getDetail(apiResponse.detail)}`
          : `${LOADING_USERS_ERROR}${httpResponse.errorMsg}`;
        this.usersLoaded = false;
      }
    }

    return Promise.resolve(result);
  }

  public getAllUsersIds(): Array<string> {
    return Array.from(this.allUsers.keys());
  }

  public getUsersMap(): Map<string, UserDS> {
    return this.allUsers;
  }

  public async updateUser(user: UserDS): Promise<UsersOperationsResponse> {
    const result = {
      success: true,
      errorMsg: ""
    };

    const httpResponse: HTTPResponse = await this.httpClient.send(HTTPMethod.POST, "/api/user/update", {
      user: this.toBackendUserDto(user)
    });
    const apiResponse = httpResponse.data as APIResponse<object>;

    if (!httpResponse.success || !apiResponse.result) {
      result.success = false;
      result.errorMsg = httpResponse.success
        ? `${UPDATE_USER_ERROR}${this.getDetail(apiResponse.detail)}`
        : `${UPDATE_USER_ERROR}${httpResponse.errorMsg}`;
    } else this.allUsers.set(user.id, user);

    return Promise.resolve(result);
  }

  public async saveUser(user: UserDS): Promise<UsersOperationsResponse> {
    const result = {
      success: true,
      errorMsg: ""
    };

    const httpResponse: HTTPResponse = await this.httpClient.send(HTTPMethod.POST, "/api/user/create", {
      user: this.toBackendUserDto(user)
    });
    const apiResponse = httpResponse.data as APIResponse<object>;

    if (!httpResponse.success || !apiResponse.result) {
      result.success = false;
      result.errorMsg = httpResponse.success
        ? `${SAVE_USER_ERROR}${this.getDetail(apiResponse.detail)}`
        : `${SAVE_USER_ERROR}${httpResponse.errorMsg}`;
    } else {
      this.allUsers.set(user.id, user);
      this.activeUsers.push(user.id);
    }

    return Promise.resolve(result);
  }

  public async deleteUsers(ids: Array<string>): Promise<UsersOperationsResponse> {
    const result = {
      success: true,
      errorMsg: ""
    };

    const httpResponse: HTTPResponse = await this.httpClient.send(HTTPMethod.POST, "/api/user/delete", {
      usersIds: ids
    });
    const apiResponse = httpResponse.data as APIResponse<object>;

    if (!httpResponse.success || !apiResponse.result) {
      result.success = false;
      result.errorMsg = httpResponse.success
        ? `${DELETE_USER_ERROR}${this.getDetail(apiResponse.detail)}`
        : `${DELETE_USER_ERROR}${httpResponse.errorMsg}`;
    } else {
      ids.forEach((deletedUserId: string) => {
        const user = this.allUsers.get(deletedUserId);
        if (user) {
          user.deleted = true;
          const toDeleteIndex = this.activeUsers.indexOf(user.id);
          if (toDeleteIndex > -1) {
            this.activeUsers.splice(toDeleteIndex, 1);
          }
        }
      });
    }

    return Promise.resolve(result);
  }

  public getActiveUsersIds(): Array<string> {
    return this.activeUsers;
  }
}
